import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check, RotateCcw } from "lucide-react";
import { BOOKING_URL } from "@/constants/site";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { QUIZ_STEPS, recommendPackage } from "@/lib/quiz";
import { iconFor } from "@/lib/icons";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const slide = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.3 } },
};

const OptionButton = ({ option, selected, onClick, stepKey }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`quiz-option-${stepKey}-${option.id}`}
    aria-pressed={selected}
    className={`group relative flex w-full items-start gap-4 border p-5 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
      selected ? "border-[#E60000] bg-[#E60000]/10" : "border-white/10 bg-[#0A0A0A] hover:border-white/30"
    }`}
  >
    <span
      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-[background-color,border-color] duration-300 ${
        selected ? "border-[#E60000] bg-[#E60000]" : "border-white/30"
      }`}
    >
      {selected && <Check size={13} strokeWidth={3} className="text-white" />}
    </span>
    <span>
      <span className="font-display block text-lg font-semibold uppercase tracking-wide text-white">
        {option.label}
      </span>
      <span className="mt-0.5 block text-sm text-zinc-400">{option.hint}</span>
    </span>
  </button>
);

const QuizResult = ({ result, onRetake }) => {
  const Icon = iconFor(result.pkg.icon);
  return (
    <motion.div
      key="result"
      variants={slide}
      initial="enter"
      animate="center"
      exit="exit"
      data-testid="quiz-result"
      className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-12"
    >
      <div className="bg-[#0A0A0A] p-7 md:col-span-7 md:p-10">
        <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]">
          <span className="h-px w-10 bg-[#E60000]" />
          Our pick for you
        </p>
        <div className="mt-6 flex items-start gap-5">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center bg-[#E60000] text-white">
            <Icon size={26} strokeWidth={1.75} />
          </span>
          <div>
            <h3 className="font-display text-3xl font-bold uppercase leading-none tracking-tight md:text-5xl" data-testid="quiz-result-package-name">
              {result.pkg.name}
            </h3>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-zinc-500">Starting at</span>
              <span className="font-display text-4xl font-bold text-[#E60000]" data-testid="quiz-result-package-price">
                {result.pkg.price}
              </span>
            </div>
          </div>
        </div>
        <ul className="mt-7 space-y-2.5">
          {result.reasons.map((r) => (
            <li key={r} className="flex items-start gap-2.5 text-sm text-zinc-300">
              <Check size={15} strokeWidth={3} className="mt-0.5 shrink-0 text-[#E60000]" />
              {r}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="quiz-book-button"
            className="group inline-flex items-center justify-center gap-2 bg-[#E60000] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.2em] text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#FF1A1A] hover:shadow-[0_12px_40px_rgba(230,0,0,0.35)]"
          >
            Book the {result.pkg.name.replace(" Package", "")}
            <ArrowUpRight size={16} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
          <button
            type="button"
            onClick={onRetake}
            data-testid="quiz-retake-button"
            className="inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-4 font-display text-sm font-semibold uppercase tracking-[0.2em] text-white transition-[border-color,color] duration-300 hover:border-[#E60000] hover:text-[#E60000]"
          >
            <RotateCcw size={15} />
            Retake
          </button>
        </div>
      </div>
      <div className="bg-[#050505] p-7 md:col-span-5 md:p-10">
        <h4 className="font-display text-sm font-semibold uppercase tracking-[0.25em] text-white">
          Worth adding
        </h4>
        {result.recommendedAddOns.length ? (
          <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-testid="quiz-result-addons">
            {result.recommendedAddOns.map((a) => {
              const AIcon = iconFor(a.icon);
              return (
                <li key={a.name} className="flex items-center justify-between gap-4 py-3.5">
                  <span className="flex items-center gap-3 text-sm text-zinc-200">
                    <AIcon size={18} className="text-[#E60000]" strokeWidth={1.75} />
                    {a.name}
                  </span>
                  <span className="font-display text-lg font-bold text-[#E60000]">{a.price}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-5 text-sm leading-relaxed text-zinc-400" data-testid="quiz-result-no-addons">
            Nothing extra needed. Your package covers it.
          </p>
        )}
        <p className="mt-6 text-xs leading-relaxed text-zinc-500">
          Final pricing depends on vehicle size and condition, confirmed with you before any work begins.
        </p>
      </div>
    </motion.div>
  );
};

export const PackageQuiz = () => {
  const { config } = useSiteConfig();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ extras: [] });
  const [done, setDone] = useState(false);
  const current = QUIZ_STEPS[step];

  const result = useMemo(
    () => (done ? recommendPackage(answers, config.packages, config.add_ons) : null),
    [done, answers, config],
  );

  const choose = (opt) => {
    if (current.single) {
      setAnswers((a) => ({ ...a, [current.key]: opt.id }));
      setTimeout(() => (step === QUIZ_STEPS.length - 1 ? setDone(true) : setStep(step + 1)), 220);
    } else {
      setAnswers((a) => {
        const set = new Set(a.extras);
        set.has(opt.id) ? set.delete(opt.id) : set.add(opt.id);
        return { ...a, extras: [...set] };
      });
    }
  };

  const retake = () => {
    setAnswers({ extras: [] });
    setStep(0);
    setDone(false);
  };

  return (
    <section id="quiz" data-testid="quiz-section" className="relative border-t border-white/10 bg-[#070707] py-24 md:py-36">
      <div className="mx-auto max-w-5xl px-5 md:px-10">
        <div className="mb-12 md:mb-16">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#E60000]"
          >
            <span className="h-px w-10 bg-[#E60000]" />
            30-second quiz
          </motion.p>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl"
          >
            Not sure which <span className="text-outline-red">detail</span>?
          </motion.h2>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400"
          >
            Answer four quick questions about your ride and we'll point you to the right package — no guesswork, no upsell.
          </motion.p>
        </div>

        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
          <AnimatePresence mode="wait">
            {done && result ? (
              <QuizResult result={result} onRetake={retake} />
            ) : (
              <motion.div key={current.key} variants={slide} initial="enter" animate="center" exit="exit" data-testid={`quiz-step-${current.key}`}>
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex gap-1.5" aria-hidden="true">
                    {QUIZ_STEPS.map((s, i) => (
                      <span
                        key={s.key}
                        className={`h-1 w-8 transition-[background-color] duration-500 ${i <= step ? "bg-[#E60000]" : "bg-white/15"}`}
                      />
                    ))}
                  </div>
                  <span className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-zinc-500" data-testid="quiz-progress">
                    {current.eyebrow} / 0{QUIZ_STEPS.length}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight md:text-4xl" data-testid="quiz-question">
                  {current.title}
                </h3>
                {!current.single && (
                  <p className="mt-2 text-sm text-zinc-500">Pick all that apply — or skip ahead.</p>
                )}
                <div className={`mt-7 grid gap-3 ${current.options.length === 4 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
                  {current.options.map((opt) => (
                    <OptionButton
                      key={opt.id}
                      option={opt}
                      stepKey={current.key}
                      selected={current.single ? answers[current.key] === opt.id : answers.extras.includes(opt.id)}
                      onClick={() => choose(opt)}
                    />
                  ))}
                </div>
                <div className="mt-7 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    data-testid="quiz-back-button"
                    className="inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-white disabled:invisible"
                  >
                    <ArrowLeft size={14} />
                    Back
                  </button>
                  {!current.single && (
                    <button
                      type="button"
                      onClick={() => setStep((s) => s + 1)}
                      data-testid="quiz-next-button"
                      className="inline-flex items-center gap-2 bg-white px-6 py-3 font-display text-xs font-semibold uppercase tracking-[0.25em] text-black transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#E60000] hover:text-white"
                    >
                      {answers.extras.length ? "Continue" : "None, skip"}
                      <ArrowUpRight size={14} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default PackageQuiz;
